// Copyright (c) 2021 WiseTime. All rights reserved.

import { chain, isEmpty, uniq, uniqBy, uniqWith } from "ramda"
import { BroadSearchPayload, SearchFilter, SearchTerms } from "../toolbar/slice"
import { Tag } from "../../../generated/legebill/type/tag_pb"
import { ActivityType } from "../../../generated/legebill/type/activity_type_pb"
import { TagCategory } from "../../../generated/legebill/type/tag_category_pb"
import { TimeRow } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { TimeGroupsCache } from "../types"
import { TagCategoriesCache, TagsCache } from "../../tags/slice"
import { ActivityTypesCache } from "../../activity-types/slice"
import { SiteId } from "../../user-context/slice"
import { notUndefined } from "../../../lib/utils"

type WithId = {
  id: string,
}

export const equalsById = (a: WithId, b: WithId): boolean => a.id === b.id

export const isSearchEmpty = (searchTerms: SearchTerms): boolean => isEmpty(searchTerms)

export const isSearchNotEmpty = (searchTerms: SearchTerms): boolean => !isSearchEmpty(searchTerms)

export const getMergedFilter = (terms: SearchTerms, currentSiteId?: string): SearchFilter => mergeFilters(Object.values(terms), currentSiteId)

// visible for test only
export const mergeFilters = (filters: Partial<SearchFilter>[], currentSiteId?: string): SearchFilter => {
  return {
    activities: uniq(chain(filter => filter.activities ?? [], filters)),
    descriptions: uniq(chain(filter => filter.descriptions ?? [], filters)),
    tags: uniqWith(equalsById,
      chain(filter => filter.tags?.filter(t=> t.siteId == currentSiteId) ?? [], filters)),
    activityTypes: uniqWith(equalsById,
      chain(filter => filter.activityTypes?.filter(t=> t.siteId == currentSiteId) ?? [], filters)),
    tagCategories: uniqWith(equalsById,
      chain(filter => filter.tagCategories?.filter(t=> t.siteId == currentSiteId) ?? [], filters)),
  }
}

const check = (label: string, description: string, term: string) => [label, description]
  .some(value => value.toLocaleLowerCase().includes(term))
export const createBroadSearch = (
  inputTerm: string,
  uniqueTags: Tag.AsObject[],
  uniqueActivityTypes: ActivityType.AsObject[],
  uniqueTagCategories: TagCategory.AsObject[],
): BroadSearchPayload => {
  const lowerCasedTerm = inputTerm.toLocaleLowerCase()
  const matchedTags = uniqueTags.filter(tag => check(tag.name, tag.description, lowerCasedTerm))
  const matchedActivityTypes = uniqueActivityTypes.filter(activityType => check(activityType.label, activityType.code, lowerCasedTerm))
  const matchedTagCategories = uniqueTagCategories.filter(tagCategory => check(tagCategory.label, tagCategory.description, lowerCasedTerm))

  return {
    term: inputTerm,
    filter: {
      activities: [inputTerm],
      descriptions: [inputTerm],
      tags: matchedTags,
      activityTypes: matchedActivityTypes,
      tagCategories: matchedTagCategories,
    }
  }
}

export const createSearchTerm = (
  term: string,
  searchTermsValues: SearchTerms,
  changedSiteTagsList: Tag.AsObject[],
  changedActivityTypesList: ActivityType.AsObject[],
  changedTagCategoriesList: TagCategory.AsObject[],
): BroadSearchPayload => {
  const tag = changedSiteTagsList.find(t => check(t.name, t.description, term))
  const prevTag = searchTermsValues[term]?.tags?.find(t => check(t.name, t.description, term))
  const prevDescription = searchTermsValues[term]?.descriptions?.find(t => t == term)
  const prevActivity = searchTermsValues[term]?.activities?.find(t => t == term)
  if (prevTag && prevDescription === undefined && prevActivity === undefined) {
    return {
      term: term,
      filter: {
        activities: [],
        activityTypes: [],
        tagCategories: [],
        descriptions: [],
        tags: [tag || prevTag],
      }
    }
  }
  const activityType = changedActivityTypesList.find(a => check(a.label, a.code, term))
  const prevActivityType = searchTermsValues[term]?.activityTypes?.find(a => check(a.label, a.code, term))
  if (prevActivityType && prevDescription === undefined && prevActivity === undefined) {
    return {
      term: term,
      filter: {
        activities: [],
        activityTypes: [activityType || prevActivityType],
        tagCategories: [],
        descriptions: [],
        tags: [],
      }
    }
  }
  const tagCategory = changedTagCategoriesList.find(t => check(t.label, t.description, term))
  const prevTagCategory = searchTermsValues[term]?.tagCategories?.find(t => check(t.label, t.description, term))
  if (prevTagCategory && prevDescription === undefined && prevActivity === undefined) {
    return {
      term: term,
      filter: {
        activities: [],
        activityTypes: [],
        tagCategories: [tagCategory || prevTagCategory],
        descriptions: [],
        tags: [],
      }
    }
  }
  return createBroadSearch(term, changedSiteTagsList, changedActivityTypesList, changedTagCategoriesList)
}

export type Suggestion = Tag.AsObject | ActivityType.AsObject | TagCategory.AsObject | string
export type SearchSuggestions = {
  tagsList: Tag.AsObject[],
  activityList: string[],
  activityTypesList: ActivityType.AsObject[],
  tagCategoriesList: TagCategory.AsObject[],
}
export const searchSuggestionsCombiner = (
  inputValue: string,
  timeRows: TimeRow.AsObject[],
  timeGroupsCache: TimeGroupsCache,
  tagsCache: TagsCache,
  activityTypesCache: ActivityTypesCache,
  tagCategoriesCache: TagCategoriesCache,
  currentSiteId: SiteId | undefined,
  searchTerms: string[],
): SearchSuggestions => {
  const timeGroupOrTimeRow = (timeRow: TimeRow.AsObject) => timeGroupsCache[timeRow.timeGroupId] || timeRow
  const filterBySearch = (suggestionName: string, suggestionDescription = ""): boolean => (
    !searchTerms.map(term => term.toLocaleLowerCase()).includes(suggestionName.toLocaleLowerCase())
    && (inputValue !== "" && (suggestionName + suggestionDescription).toLocaleLowerCase()
      .indexOf(inputValue.toLocaleLowerCase()) >= 0
    )
  )
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" })
  const getUniqList = <T,>(items: T[], getName: (arg: T) => string, getDescription?: (arg: T) => string): T[] => (
    uniqBy(getName, items)
      .filter(i => filterBySearch(getName(i), getDescription && getDescription(i)))
      .sort((a, b) => collator.compare(getName(a), getName(b)))
  )
  const activityList = getUniqList(timeRows.map(timeRow => timeRow.activity), a => a)
  const tagsList = getUniqList(
    chain(
      timeRow => timeGroupOrTimeRow(timeRow).tagsList
        .map(item => tagsCache[item.id])
        .filter(notUndefined)
        .filter(
          (tag) => tag?.siteId === currentSiteId
        ),
      timeRows,
    ),
    (tag) => tag.name,
    (tag) => tag.description,
  )
  const activityTypesList = getUniqList(
    chain(
      timeRow => timeGroupOrTimeRow(timeRow).activityTypesList
        .map(item => activityTypesCache[item.id])
        .filter(notUndefined)
        .filter(
          activityType => activityType?.siteId === currentSiteId
        ),
      timeRows,
    ),
    activityType => activityType.label,
    activityType => activityType.code,
  )
  const tagCategoriesList = getUniqList(
    chain(
      timeRow => timeGroupOrTimeRow(timeRow).tagCategoriesList
        .map(item => tagCategoriesCache[item.id])
        .filter(notUndefined)
        .filter(
          tagCategory => tagCategory.siteId === currentSiteId
        ),
      timeRows,
    ),
    tagCategory => tagCategory.label,
    tagCategory => tagCategory.description,
  )
  
  return {
    tagsList,
    activityList,
    activityTypesList,
    tagCategoriesList,
  }
}
